
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { type TelemetryData } from './utils/telemetryDataUtils';
import { cn } from '@/lib/utils';
import { getBorderColorClass } from './utils/chartUtils';
import { Hash } from 'lucide-react';
import { Device } from '@/components/device-health/gis-map/types';

interface TelemetryHistoryTableProps {
  telemetryData: TelemetryData[];
  selectedSection?: string | null;
  onCardClick?: (section: string) => void;
  sectionName: string;
  telemetryType: 'energy' | 'gas' | 'water';
  deviceId?: string;
  deviceData?: Device | null;
}

const TelemetryHistoryTable: React.FC<TelemetryHistoryTableProps> = ({
  telemetryData,
  selectedSection,
  onCardClick,
  sectionName,
  telemetryType,
  deviceId,
  deviceData
}) => {
  const formatTimestamp = (timestamp: string) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  // Filter telemetry data if a specific device ID is provided
  const filteredData = deviceId 
    ? telemetryData.filter((_, index) => {
        const dataDeviceId = `${telemetryType.toUpperCase()}-TELEMETRY-${index + 1}`;
        return dataDeviceId === deviceId;
      })
    : telemetryData;

  return (
    <Card 
      className={cn(
        "cursor-pointer hover:border-blue-500 transition-colors", 
        selectedSection === sectionName && getBorderColorClass(telemetryType)
      )}
      onClick={() => onCardClick && onCardClick(sectionName)}
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {deviceData ? `Telemetry History: ${deviceData.name}` : 'Telemetry History'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Device ID</TableHead>
                <TableHead>Smart Meters (Online/Total)</TableHead>
                <TableHead>Gateways (Online/Total)</TableHead>
                <TableHead>Avg. Latency</TableHead>
                <TableHead>Data Transferred</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredData.map((data, index) => {
                const deviceId = `${telemetryType.toUpperCase()}-TELEMETRY-${index + 1}`;
                return (
                  <TableRow key={data.timestamp}>
                    <TableCell>{formatTimestamp(data.timestamp)}</TableCell>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center">
                        <Hash className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
                        {deviceId}
                      </div>
                    </TableCell>
                    <TableCell>
                      {data.smartMeters.online}/{data.smartMeters.online + data.smartMeters.warning + data.smartMeters.critical + data.smartMeters.offline}
                    </TableCell>
                    <TableCell>
                      {data.gateways.online}/{data.gateways.online + data.gateways.warning + data.gateways.critical + data.gateways.offline}
                    </TableCell>
                    <TableCell>{data.gateways.averageLatency} ms</TableCell>
                    <TableCell>{data.gateways.dataTransferred.toFixed(1)} MB</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default TelemetryHistoryTable;
